/**
 * Las piezas comunes de las pantallas de DEENEX Activa (auditoría de diseño del 18/10/2026): cada módulo resolvía a su
 * manera el ancho, el encabezado, el «no hay nada» y los avisos, y la app no se sentía una sola.
 *
 * - `Pagina`: un solo ancho. Las pantallas de formulario usan `angosta`, que acorta el contenido sin moverlo: el título
 *   queda siempre en el mismo lugar al pasar de una pantalla a otra.
 * - `EncabezadoPagina`: antetítulo con el punto, título, bajada y las acciones a la derecha (abajo en el celular).
 * - `EstadoVacio`: ícono, qué falta y la acción que lo resuelve.
 * - `Aviso`: un renglón con tono (info, atención, error, listo), con su acción opcional.
 */

import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

export function Pagina({ angosta = false, children }: { angosta?: boolean; children: ReactNode }) {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <div className={`space-y-6 ${angosta ? "max-w-4xl" : ""}`}>{children}</div>
    </div>
  );
}

export function Antetitulo({ children }: { children: ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
      <span aria-hidden="true">● </span>
      {children}
    </p>
  );
}

export function EncabezadoPagina({
  antetitulo,
  titulo,
  bajada,
  acciones,
  children,
}: {
  antetitulo?: ReactNode;
  titulo: ReactNode;
  bajada?: ReactNode;
  acciones?: ReactNode;
  /** debajo de la bajada: datos chicos de la página (antigüedad de la base, última actualización) */
  children?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
      <div className="min-w-0 max-w-3xl space-y-1.5">
        {antetitulo && <Antetitulo>{antetitulo}</Antetitulo>}
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{titulo}</h1>
        {bajada && <div className="text-sm sm:text-base text-slate-600">{bajada}</div>}
        {children}
      </div>
      {acciones && <div className="flex flex-wrap items-center gap-2">{acciones}</div>}
    </header>
  );
}

export function EstadoVacio({
  icono: IconoVacio,
  titulo,
  children,
  accion,
  testId,
}: {
  icono: LucideIcon;
  titulo: ReactNode;
  children?: ReactNode;
  accion?: ReactNode;
  testId?: string;
}) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-10 flex flex-col items-center gap-3 text-center">
      <span className="w-12 h-12 rounded-2xl bg-violet-50 text-violet-600 flex items-center justify-center" aria-hidden="true">
        <IconoVacio size={22} />
      </span>
      <h2 className="text-base font-semibold text-slate-900">{titulo}</h2>
      {children && <div className="text-sm text-slate-600 max-w-md">{children}</div>}
      {accion && <div className="flex flex-wrap justify-center gap-2 pt-1">{accion}</div>}
    </div>
  );
}

const TONOS_AVISO = {
  info: "border-violet-200 bg-violet-50 text-violet-900",
  atencion: "border-amber-200 bg-amber-50 text-amber-900",
  error: "border-red-200 bg-red-50 text-red-800",
  listo: "border-emerald-200 bg-emerald-50 text-emerald-900",
} as const;

export function Aviso({
  tono = "info",
  titulo,
  children,
  accion,
  testId,
}: {
  tono?: keyof typeof TONOS_AVISO;
  titulo?: ReactNode;
  children?: ReactNode;
  accion?: ReactNode;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      role={tono === "error" ? "alert" : "status"}
      className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-xl border px-4 py-3 text-sm ${TONOS_AVISO[tono]}`}
    >
      <div className="min-w-0 space-y-0.5">
        {titulo && <p className="font-semibold">{titulo}</p>}
        {children && <div>{children}</div>}
      </div>
      {accion && <div className="flex flex-wrap items-center gap-2">{accion}</div>}
    </div>
  );
}
